import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import {
  Send, ShieldCheck, Inbox, ChevronDown, Check, Users, Building2, Home,
  Bell, MessageSquare, Sparkles, Activity, FileText, LifeBuoy, ClipboardList, BookOpen, Eraser, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { relativeTime } from "@/lib/utils";
import Avatar from "@/components/Avatar";
import AdminVerificationTab from "@/components/AdminVerificationTab";
import AdminGuideTab from "@/components/AdminGuideTab";
import AdminActivityCharts from "@/components/AdminActivityCharts";
import ExportButtons from "@/components/ExportButtons";
import CleanupDialog from "@/components/AdminCleanup";
import BulkDeleteList from "@/components/BulkDeleteList";
import TrashTab from "@/components/AdminTrashTab";
import ReviewStatusBadge, { REVIEW_STATUS_OPTIONS, reviewStatus } from "@/components/ReviewStatusBadge";

const TICKET_STATUSES = [
  { key: "open", label: "Open", color: "bg-sky-50 text-sky-700 border-sky-200" },
  { key: "in_progress", label: "In Progress", color: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "resolved", label: "Resolved", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];

const TABS = [
  { key: "overview", label: "Overview", icon: Activity },
  { key: "users", label: "Users", icon: Users },
  { key: "verification", label: "Verification", icon: ShieldCheck },
  { key: "developments", label: "Developments", icon: Building2 },
  { key: "units", label: "Units", icon: Home },
  { key: "interests", label: "Interests", icon: Sparkles },
  { key: "conversations", label: "Conversations", icon: MessageSquare },
  { key: "updates", label: "Updates", icon: FileText },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "support", label: "Support", icon: LifeBuoy },
  { key: "guide", label: "Investor Guide", icon: BookOpen },
  { key: "trash", label: "Recently deleted", icon: Trash2 },
  { key: "audit", label: "Audit Log", icon: ClipboardList },
];

function StatusPill({ status }) {
  const s = TICKET_STATUSES.find((o) => o.key === status) || TICKET_STATUSES[0];
  return <span data-testid={`admin-status-${s.key}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${s.color}`}>{s.label}</span>;
}

export default function AdminSupport() {
  const { user, loading: authLoading } = useAuth();
  const allowed = user ? !!user.is_admin : null;
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";

  function setTab(next) {
    params.set("tab", next);
    setParams(params, { replace: true });
  }

  if (authLoading || allowed === null) return <div className="max-w-6xl mx-auto text-center py-20 text-sm text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!allowed) {
    return (
      <div className="max-w-lg mx-auto text-center py-24">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-neutral-100 text-neutral-500 flex items-center justify-center mb-4"><ShieldCheck size={22} /></div>
        <h1 className="text-xl font-bold text-neutral-900">Admin only</h1>
        <p className="text-sm text-neutral-500 mt-2">This page is restricted to Propera administrators.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Admin Console</p>
      <h1 className="text-4xl font-bold text-neutral-900 mt-2" data-testid="admin-page-title">{tab === "overview" ? "Platform overview" : (TABS.find((t) => t.key === tab)?.label || "Admin")}</h1>
      {tab === "overview" && <p className="text-neutral-500 mt-2">Full oversight of every account, listing, conversation and support ticket on Propera. Click any card to open it.</p>}

      <div className="flex gap-1 border-b border-neutral-200 mt-8 overflow-x-auto no-scrollbar lg:hidden">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 sm:px-5 py-3 text-sm font-semibold transition border-b-2 whitespace-nowrap inline-flex items-center gap-2 ${tab === t.key ? "text-propera border-propera" : "text-neutral-500 border-transparent hover:text-neutral-900"}`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 lg:mt-6">
        {tab === "overview" && <OverviewTab />}
        {tab === "users" && <UsersTab />}
        {tab === "verification" && <AdminVerificationTab />}
        {tab === "developments" && <DevelopmentsTab />}
        {tab === "units" && <UnitsTab />}
        {tab === "interests" && <InterestsTab />}
        {tab === "conversations" && <ConversationsTab />}
        {tab === "updates" && <UpdatesTab />}
        {tab === "notifications" && <NotificationsTab />}
        {tab === "support" && <SupportTab />}
        {tab === "guide" && <AdminGuideTab />}
        {tab === "trash" && <TrashTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, to, testId }) {
  const inner = (
    <>
      <p className={`text-[10px] uppercase tracking-wider font-semibold inline-block px-2 py-0.5 rounded-full ${accent}`}>{label}</p>
      <p className="text-2xl font-bold text-neutral-900 tabular mt-2">{value ?? 0}</p>
      {to && <p className="text-[11px] text-neutral-400 group-hover:text-propera mt-1 transition-colors">View →</p>}
    </>
  );
  return to ? (
    <Link to={to} data-testid={testId} className="group soft-card p-4 soft-card-hover block cursor-pointer">{inner}</Link>
  ) : (
    <div className="soft-card p-4" data-testid={testId}>{inner}</div>
  );
}

/* ------- Overview ------- */
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    (async () => {
      const [s, a] = await Promise.all([api.get("/admin/dashboard/stats"), api.get("/admin/activity")]);
      setStats(s.data); setActivity(a.data);
    })();
  }, []);
  if (!stats) return <p className="text-sm text-neutral-500">Loading…</p>;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Users" value={stats.users?.total} accent="bg-neutral-100 text-neutral-800" to="/admin?tab=users" testId="admin-stat-users" />
        <StatCard label="Investors" value={stats.users?.investors} accent="bg-blue-50 text-blue-700" to="/admin?tab=users&role=investor" testId="admin-stat-investors" />
        <StatCard label="Developers" value={stats.users?.developers} accent="bg-emerald-50 text-emerald-700" to="/admin?tab=users&role=developer" testId="admin-stat-developers" />
        <StatCard label="Verified developers" value={stats.users?.verified} accent="bg-propera-light text-propera" to="/admin?tab=users&role=developer&verified=true" testId="admin-stat-verified" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
        <StatCard label="Developments" value={stats.developments?.total} accent="bg-indigo-50 text-indigo-700" to="/admin?tab=developments" testId="admin-stat-developments" />
        <StatCard label="Pending review" value={stats.developments?.pending_review} accent="bg-amber-50 text-amber-700" to="/admin?tab=developments&status=under_review" testId="admin-stat-pending-review" />
        <StatCard label="Docs to review" value={stats.documents_pending} accent="bg-rose-50 text-rose-700" to="/admin?tab=verification" testId="admin-stat-docs-pending" />
        <StatCard label="Units" value={stats.units} accent="bg-violet-50 text-violet-700" to="/admin?tab=units" testId="admin-stat-units" />
        <StatCard label="Interests" value={stats.interests} accent="bg-sky-50 text-sky-700" to="/admin?tab=interests" testId="admin-stat-interests" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <StatCard label="Conversations" value={stats.conversations} accent="bg-teal-50 text-teal-700" to="/admin?tab=conversations" testId="admin-stat-conversations" />
        <StatCard label="Messages" value={stats.messages} accent="bg-cyan-50 text-cyan-700" to="/admin?tab=conversations" testId="admin-stat-messages" />
        <StatCard label="Support tickets" value={stats.tickets} accent="bg-orange-50 text-orange-700" to="/admin?tab=support" testId="admin-stat-tickets" />
        <StatCard label="Open tickets" value={stats.open_tickets} accent="bg-red-50 text-red-700" to="/admin?tab=support&status=open" testId="admin-stat-open-tickets" />
      </div>

      <AdminActivityCharts />

      <h2 className="text-lg font-bold text-neutral-900 mt-8 flex items-center gap-2"><Activity size={16} /> Recent activity</h2>
      <div className="soft-card divide-y divide-neutral-100 mt-3" data-testid="admin-activity">
        {activity.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No activity yet.</p>}
        {activity.map((a, idx) => (
          <div key={idx} className="p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-propera-light text-propera flex items-center justify-center text-[11px] font-bold uppercase flex-shrink-0">
              {a.kind.slice(0, 2)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-neutral-900 truncate">{a.title}</p>
              <p className="text-xs text-neutral-500">{a.kind.replace("_", " ")} · {relativeTime(a.at)}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ------- Users ------- */
function useParamState(key, fallback) {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) || fallback;
  const set = (v) => {
    const next = new URLSearchParams(params);
    if (!v || v === fallback) next.delete(key); else next.set(key, v);
    setParams(next, { replace: true });
  };
  return [value, set, params, setParams];
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [role, , params, setParams] = useParamState("role", "all");
  const verified = params.get("verified");
  const [q, setQ] = useState("");
  const [cleanupOpen, setCleanupOpen] = useState(false);
  function setRole(r) {
    const next = new URLSearchParams(params);
    if (r === "all") next.delete("role"); else next.set("role", r);
    next.delete("verified");
    setParams(next, { replace: true });
  }

  useEffect(() => { load(); }, [role, verified]);
  async function load() {
    const params = new URLSearchParams();
    if (role !== "all") params.set("role", role);
    if (verified) params.set("verified", verified);
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/users?${params}`);
    setUsers(data);
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 items-center">
        {["all", "investor", "developer"].map((r) => (
          <button key={r} data-testid={`admin-users-filter-${r}`} onClick={() => setRole(r)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border capitalize ${role === r && !verified ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{r === "all" ? "All" : `${r}s`}</button>
        ))}
        <button data-testid="admin-users-filter-verified" onClick={() => { const n = new URLSearchParams(params); n.set("role", "developer"); n.set("verified", "true"); setParams(n, { replace: true }); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${verified ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>Verified developers</button>
        <span className="text-xs text-neutral-500" data-testid="admin-users-count">{users.length} shown</span>
        <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / email…" className="ml-auto px-3 py-1.5 rounded-full border border-neutral-200 text-sm w-52" />
        <ExportButtons kind="users" params={{ role, verified, q }} />
        <button data-testid="cleanup-open" onClick={() => setCleanupOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"><Eraser size={12} /> Remove test data</button>
      </div>
      <CleanupDialog open={cleanupOpen} onClose={() => setCleanupOpen(false)} onDone={load} />
      <BulkDeleteList
        kind="users"
        noun="account"
        items={users.map((u) => ({ ...u, __label: u.name }))}
        lockedIds={me ? [me.id] : []}
        deletePath={(id) => `/admin/users/${id}`}
        bulkPath="/admin/users/bulk-delete"
        onDone={load}
        empty="No users match."
        header={<div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3"><span>User</span><span className="hidden md:block">Role</span><span className="hidden md:block">Country</span><span className="hidden md:block">Joined</span><span>Actions</span></div>}
        renderRow={(u) => (
          <div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 items-center text-sm" data-testid={`admin-user-${u.id}`}>
            <Link to={`/u/${u.id}`} className="flex items-center gap-3 hover:text-propera min-w-0">
              <Avatar name={u.name} src={u.avatar} size={32} />
              <div className="min-w-0"><p className="font-semibold truncate">{u.name}</p><p className="text-xs text-neutral-500 truncate">{u.email}<span className="md:hidden capitalize"> · {u.role}</span></p></div>
            </Link>
            <span className="hidden md:block capitalize">{u.role}{u.verified ? <span className="ml-1 text-[10px] text-propera">· verified</span> : null}{u.suspended ? <span className="ml-1 text-[10px] text-red-600">· suspended</span> : null}</span>
            <span className="hidden md:block text-neutral-700">{u.country || "—"}</span>
            <span className="hidden md:block text-xs text-neutral-500">{relativeTime(u.created_at)}</span>
            <UserActions user={u} onDone={load} />
          </div>
        )}
      />
    </>
  );
}

/* ------- Developments ------- */
function DevelopmentsTab() {
  const [devs, setDevs] = useState([]);
  const [status, setStatus] = useParamState("status", "all");
  useEffect(() => { load(); }, [status]);
  async function load() {
    const p = new URLSearchParams(); if (status !== "all") p.set("review_status", status);
    const { data } = await api.get(`/admin/developments?${p}`);
    setDevs(data);
  }

  async function setReviewStatus(devId, next) {
    try {
      await api.patch(`/admin/developments/${devId}/review-status`, { status: next });
      toast.success(`Updated to ${reviewStatus(next).label}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update status"); }
  }

  const FILTERS = [
    { k: "all", label: "All" },
    ...REVIEW_STATUS_OPTIONS.map((o) => ({ k: o.key, label: o.label })),
  ];
  return (
    <>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        {FILTERS.map((s) => (
          <button key={s.k} data-testid={`admin-dev-filter-${s.k}`} onClick={() => setStatus(s.k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${status === s.k ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{s.label}</button>
        ))}
        <div className="ml-auto"><ExportButtons kind="developments" params={{ review_status: status }} /></div>
      </div>
      <BulkDeleteList
        kind="devs"
        noun="development"
        items={devs.map((d) => ({ ...d, __label: d.name }))}
        deletePath={(id) => `/admin/developments/${id}`}
        bulkPath="/admin/developments/bulk-delete"
        onDone={load}
        empty="No developments."
        header={<div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1.2fr_1fr_.5fr_.5fr_auto] gap-3"><span>Name</span><span className="hidden md:block">Developer</span><span className="hidden md:block">Status</span><span className="hidden md:block">Units</span><span className="hidden md:block">Interests</span><span>Actions</span></div>}
        renderRow={(d) => (
          <div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1.2fr_1fr_.5fr_.5fr_auto] gap-3 items-center text-sm" data-testid={`admin-dev-${d.id}`}>
            <div className="min-w-0"><Link to={`/development/${d.id}`} className="font-semibold hover:text-propera truncate block">{d.name}</Link><p className="text-xs text-neutral-500 truncate">{d.city}, {d.country}</p></div>
            <Link to={`/u/${d.developer_id}`} className="hidden md:block hover:text-propera truncate">{d.developer_name}</Link>
            <span className="hidden md:block"><ReviewStatusBadge status={d.review_status} /></span>
            <span className="hidden md:block">{d.unit_count}</span>
            <span className="hidden md:block">{d.interest_count}</span>
            <ReviewStatusPicker current={d.review_status} onChange={(st) => setReviewStatus(d.id, st)} />
          </div>
        )}
      />
    </>
  );
}

function ReviewStatusPicker({ current, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button type="button" data-testid="review-status-picker" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border bg-white border-neutral-200 hover:border-neutral-300">
        Change <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-40 mt-2 w-60 rounded-2xl border border-neutral-200 bg-white shadow-lg p-1">
            {REVIEW_STATUS_OPTIONS.map((o) => (
              <button key={o.key} data-testid={`review-status-option-${o.key}`} onClick={() => { onChange(o.key); setOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm hover:bg-neutral-50">
                <ReviewStatusBadge status={o.key} />
                {o.key === current && <Check size={14} className="text-propera" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------- Units ------- */
function UnitsTab() {
  const [units, setUnits] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/units"); setUnits(data); })(); }, []);
  return (
    <div className="soft-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider">
          <tr><th className="text-left px-5 py-3">Label</th><th className="text-left px-5 py-3">Development</th><th className="text-left px-5 py-3">Beds</th><th className="text-left px-5 py-3">Price</th><th className="text-left px-5 py-3">Status</th></tr>
        </thead>
        <tbody>
          {units.map((u) => (
            <tr key={u.id} className="border-t border-neutral-100" data-testid={`admin-unit-${u.id}`}>
              <td className="px-5 py-3 font-semibold">{u.label}</td>
              <td className="px-5 py-3"><Link to={`/development/${u.development_id}`} className="hover:text-propera">{u.development_name}</Link></td>
              <td className="px-5 py-3">{u.bedrooms}</td>
              <td className="px-5 py-3 tabular">${u.price?.toLocaleString?.() || u.price}</td>
              <td className="px-5 py-3 text-xs capitalize">{u.status || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {units.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No units.</p>}
    </div>
  );
}

/* ------- Interests ------- */
const INTEREST_STAGES = [
  ["all", "All"], ["new_interest", "New Interest"], ["contact_attempted", "Contact Attempted"], ["contact_made", "Contact Made"],
  ["meeting_scheduled", "Meeting Scheduled"], ["negotiating", "Negotiating"], ["invested", "Invested"], ["not_proceeding", "Not Proceeding"],
];

function InterestsTab() {
  const [items, setItems] = useState([]);
  const [stage, setStage] = useParamState("status", "all");
  useEffect(() => { (async () => { const { data } = await api.get(`/admin/interests${stage === "all" ? "" : `?status=${stage}`}`); setItems(data); })(); }, [stage]);
  return (
    <>
    <div className="flex gap-2 mb-4 flex-wrap items-center">
      {INTEREST_STAGES.map(([k, label]) => (
        <button key={k} data-testid={`admin-interest-filter-${k}`} onClick={() => setStage(k)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${stage === k ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{label}</button>
      ))}
      <div className="ml-auto"><ExportButtons kind="interests" params={{ status: stage }} /></div>
    </div>
    <div className="soft-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase tracking-wider">
          <tr><th className="text-left px-5 py-3">Investor</th><th className="text-left px-5 py-3">Development</th><th className="text-left px-5 py-3">Developer</th><th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">When</th></tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-t border-neutral-100" data-testid={`admin-interest-${i.id}`}>
              <td className="px-5 py-3"><Link to={`/u/${i.investor?.id}`} className="hover:text-propera">{i.investor?.name}</Link></td>
              <td className="px-5 py-3"><Link to={`/development/${i.development_id}`} className="hover:text-propera">{i.development?.name}</Link></td>
              <td className="px-5 py-3"><Link to={`/u/${i.developer?.id}`} className="hover:text-propera">{i.developer?.name}</Link></td>
              <td className="px-5 py-3 text-xs">{(i.status || "").replace(/_/g, " ")}</td>
              <td className="px-5 py-3 text-xs text-neutral-500">{relativeTime(i.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No interests yet.</p>}
    </div>
    </>
  );
}

/* ------- Conversations ------- */
function ConversationsTab() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/conversations"); setConvos(data); })(); }, []);
  useEffect(() => {
    if (!active) return;
    (async () => { const { data } = await api.get(`/admin/conversations/${active.id}/messages`); setMsgs(data); })();
  }, [active]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-320px)] min-h-[440px]">
      <div className="soft-card p-2 overflow-y-auto space-y-1">
        {convos.map((c) => (
          <button key={c.id} data-testid={`admin-convo-${c.id}`} onClick={() => setActive(c)} className={`w-full text-left px-3 py-3 rounded-xl ${active?.id === c.id ? "bg-propera-light" : "hover:bg-neutral-50"}`}>
            <p className="text-sm font-semibold truncate">{(c.participant_details || []).map((p) => p?.name).filter(Boolean).join(" · ")}</p>
            <p className="text-xs text-neutral-500 truncate">{c.development?.name || "Direct"}</p>
            <p className="text-[10px] text-neutral-400 mt-0.5">{c.message_count} msgs · {relativeTime(c.updated_at)}</p>
          </button>
        ))}
        {convos.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No conversations.</p>}
      </div>
      <div className="md:col-span-2 soft-card p-5 flex flex-col overflow-hidden">
        {active ? (
          <>
            <p className="font-semibold border-b border-neutral-100 pb-3">{(active.participant_details || []).map((p) => p?.name).join(" · ")}</p>
            <div className="flex-1 overflow-y-auto py-4 space-y-2">
              {msgs.map((m) => {
                if (m.kind === "system") return <div key={m.id} className="text-center text-[11px] text-neutral-500 uppercase tracking-wider py-2">{m.text}</div>;
                return (
                  <div key={m.id} className="soft-card p-3 bg-neutral-50/60">
                    <p className="text-[11px] text-neutral-500">from {m.sender_id?.slice(0, 8)} · {relativeTime(m.created_at)}</p>
                    <p className="text-sm text-neutral-800 whitespace-pre-line mt-1">{m.text}</p>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-sm text-neutral-500 flex-1 flex items-center justify-center">Select a conversation</p>
        )}
      </div>
    </div>
  );
}

/* ------- Updates ------- */
function UpdatesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/updates"); setItems(data); })(); }, []);
  return (
    <div className="space-y-3">
      {items.map((u) => (
        <div key={u.id} className="soft-card p-4" data-testid={`admin-update-${u.id}`}>
          <p className="text-xs uppercase tracking-wider text-neutral-500">{u.development_name} · {relativeTime(u.created_at)}</p>
          <p className="font-semibold mt-1">{u.title}</p>
          <p className="text-sm text-neutral-600 whitespace-pre-line mt-1">{u.body}</p>
          {(u.unit_ids?.length || 0) > 0 && (
            <p className="text-[11px] text-propera mt-2">Unit-specific ({u.unit_ids.length})</p>
          )}
        </div>
      ))}
      {items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No updates.</p>}
    </div>
  );
}

/* ------- Notifications ------- */
function NotificationsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/notifications"); setItems(data); })(); }, []);
  return (
    <div className="soft-card divide-y divide-neutral-100">
      {items.map((n) => (
        <div key={n.id} className="p-4" data-testid={`admin-notif-${n.id}`}>
          <p className="text-xs text-neutral-500">{n.type} · to {n.user_id.slice(0, 8)} · {relativeTime(n.created_at)}</p>
          <p className="text-sm font-semibold mt-1">{n.title}</p>
          <p className="text-xs text-neutral-600">{n.body}</p>
        </div>
      ))}
      {items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">No notifications.</p>}
    </div>
  );
}

/* ------- Support ------- */
function SupportTab() {
  const [filter, setFilter, sp] = useParamState("status", "all");
  const ticketParam = sp.get("ticket");
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0, total: 0 });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);

  useEffect(() => { load(); }, [filter, ticketParam]);
  async function load() {
    try {
      const [t, s] = await Promise.all([
        api.get(`/admin/support/tickets${filter === "all" ? "" : `?status=${filter}`}`),
        api.get("/admin/support/stats"),
      ]);
      setTickets(t.data); setStats(s.data);
      const wanted = ticketParam && !active ? (t.data.find((x) => x.id === ticketParam) || (await api.get(`/support/tickets/${ticketParam}`).then((r) => r.data).catch(() => null))) : null;
      if (wanted) setActive(wanted);
      else if (t.data.length) {
        if (!active) setActive(t.data[0]);
        else {
          const r = t.data.find((x) => x.id === active.id);
          if (r) setActive(r);
        }
      } else setActive(null);
    } catch { /* ignore */ }
  }

  async function updateStatus(next) {
    if (!active || changingStatus) return;
    setChangingStatus(true);
    try { await api.patch(`/support/tickets/${active.id}/status`, { status: next }); toast.success(`Ticket marked ${next.replace("_"," ")}`); await load(); }
    catch { toast.error("Could not update status"); }
    finally { setChangingStatus(false); }
  }

  async function sendReply() {
    if (!active || reply.trim().length === 0) return;
    try {
      const { data } = await api.post(`/support/tickets/${active.id}/messages`, { body: reply.trim() });
      setActive(data); setReply(""); await load();
    } catch { toast.error("Could not send reply"); }
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open" value={stats.open} accent="bg-sky-50 text-sky-700" />
        <StatCard label="In Progress" value={stats.in_progress} accent="bg-amber-50 text-amber-800" />
        <StatCard label="Resolved" value={stats.resolved} accent="bg-emerald-50 text-emerald-700" />
        <StatCard label="Total" value={stats.total} accent="bg-neutral-100 text-neutral-800" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {[{ k: "all", label: "All" }, ...TICKET_STATUSES.map((s) => ({ k: s.key, label: s.label }))].map((f) => (
          <button key={f.k} data-testid={`admin-filter-${f.k}`} onClick={() => setFilter(f.k)} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${filter === f.k ? "bg-propera text-white border-propera" : "bg-white border-neutral-200"}`}>{f.label}</button>
        ))}
      </div>

      {tickets.length === 0 ? (
        <div className="soft-card p-12 text-center mt-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-4"><Inbox size={22} /></div>
          <p className="text-lg font-semibold text-neutral-900">No tickets</p>
          <p className="text-sm text-neutral-500 mt-1">There&apos;s nothing in this queue right now.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-450px)] min-h-[480px]">
          <div className="soft-card p-2 overflow-y-auto space-y-1">
            {tickets.map((t) => (
              <button key={t.id} data-testid={`admin-ticket-${t.id}`} onClick={() => setActive(t)} className={`w-full text-left px-3 py-3 rounded-xl ${active?.id === t.id ? "bg-propera-light" : "hover:bg-neutral-50"}`}>
                <div className="flex items-center gap-2 justify-between">
                  <p className="text-sm font-semibold truncate flex-1">{t.subject}</p>
                  <StatusPill status={t.status} />
                </div>
                <p className="text-xs text-neutral-500 truncate mt-0.5">from {t.user_name || t.user_email}</p>
                <p className="text-[10px] text-neutral-400 mt-0.5">{relativeTime(t.updated_at)} · {t.category}</p>
              </button>
            ))}
          </div>

          <div className="md:col-span-2 soft-card p-5 flex flex-col overflow-hidden">
            {active ? (
              <>
                <div className="flex items-center justify-between gap-2 border-b border-neutral-100 pb-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-neutral-900 truncate">{active.subject}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      <Link to={`/u/${active.user_id}`} className="hover:text-propera">{active.user_name}</Link> · {active.user_email} · {active.category}
                    </p>
                  </div>
                  <StatusPicker current={active.status} onChange={updateStatus} disabled={changingStatus} />
                </div>
                <div className="flex-1 overflow-y-auto py-4 space-y-3">
                  {(active.messages || []).map((m) => {
                    if (m.author_role === "system") return <div key={m.id} className="flex justify-center"><span data-testid={`admin-msg-system-${m.id}`} className="text-[11px] uppercase tracking-wider text-neutral-500 bg-neutral-100 rounded-full px-3 py-1">{m.body}</span></div>;
                    const isAdmin = m.author_role === "admin";
                    return (
                      <div key={m.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${isAdmin ? "bg-propera text-white rounded-br-md" : "bg-neutral-100 text-neutral-800 rounded-bl-md"}`}>
                          <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${isAdmin ? "text-white/80" : "text-propera"}`}>{isAdmin ? "You (admin)" : (active.user_name || "User")}</p>
                          {m.body}
                          <p className={`text-[10px] mt-1 ${isAdmin ? "text-white/70" : "text-neutral-500"}`}>{relativeTime(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-neutral-100">
                  <input data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to user…" className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none text-sm" />
                  <button data-testid="admin-reply-send" onClick={sendReply} className="bg-propera hover:bg-propera-hover text-white p-3 rounded-full">
                    <Send size={16} />
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500 text-center flex-1 flex items-center justify-center">Select a ticket</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function StatusPicker({ current, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const currentOpt = TICKET_STATUSES.find((s) => s.key === current) || TICKET_STATUSES[0];
  return (
    <div className="relative">
      <button data-testid="admin-status-picker" disabled={disabled} onClick={() => setOpen((v) => !v)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${currentOpt.color} ${disabled ? "opacity-70" : "hover:opacity-90"}`}>
        {currentOpt.label} <ChevronDown size={12} />
      </button>
      {open && !disabled && (
        <div className="absolute right-0 mt-2 w-44 rounded-2xl border border-neutral-200 bg-white shadow-lg p-1 z-20">
          {TICKET_STATUSES.map((o) => (
            <button key={o.key} data-testid={`admin-status-set-${o.key}`} onClick={() => { onChange(o.key); setOpen(false); }} className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm hover:bg-neutral-50">
              <span>{o.label}</span>
              {o.key === current && <Check size={14} className="text-propera" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function UserActions({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  async function run(action, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/action`, { action });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)}d ${user.name}`);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || `Could not ${action}`); }
    finally { setBusy(false); }
  }
  if (user.suspended) {
    return (
      <button data-testid={`user-reactivate-${user.id}`} disabled={busy} onClick={() => run("reactivate")} className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-60">
        Reactivate
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {user.role === "developer" && !user.verified && (
        <button data-testid={`user-verify-${user.id}`} disabled={busy} onClick={() => run("verify")} className="px-3 py-1 rounded-full text-xs font-semibold bg-propera-light text-propera border border-propera/30 hover:opacity-90 disabled:opacity-60">
          Verify
        </button>
      )}
      <button data-testid={`user-suspend-${user.id}`} disabled={busy} onClick={() => run("suspend", `Suspend ${user.name}? They won't be able to use the platform.`)} className="px-3 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-60">
        Suspend
      </button>
    </div>
  );
}

function AuditTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { const { data } = await api.get("/admin/audit"); setItems(data); })(); }, []);
  return (
    <div className="soft-card divide-y divide-neutral-100">
      {items.map((a) => (
        <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`audit-${a.id}`}>
          <div className="w-9 h-9 rounded-xl bg-propera-light text-propera flex items-center justify-center text-[10px] font-bold uppercase flex-shrink-0">
            {a.action.split(".")[0].slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-neutral-900"><span className="font-semibold">{a.action}</span> · {a.target_type} <span className="text-neutral-500">{a.target_id?.slice(0, 8)}…</span></p>
            <p className="text-xs text-neutral-500 mt-0.5">by {a.actor?.name || "unknown"} · {relativeTime(a.created_at)}</p>
            {a.meta && Object.keys(a.meta).length > 0 && (
              <pre className="text-[11px] text-neutral-600 bg-neutral-50 rounded-xl p-2 mt-2 overflow-x-auto">{JSON.stringify(a.meta, null, 2)}</pre>
            )}
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">Audit log is empty.</p>}
    </div>
  );
}
